/* iter-72c — Chapter-scoped suggestions hook.
 *
 * Extracted from EditorPanel so both the manuscript body (for inline
 * highlighting) and the SuggestionsDrawer can share a single source of
 * truth for pending edit/comment anchors. Returns the raw pending
 * suggestions plus a refresh function; consumers derive whatever shape
 * they need (highlight ranges, drawer list, badge counts, etc.).
 *
 * iter-72d — Real-time push. The hook opens an EventSource against
 * `/api/projects/{pid}/chapters/{cid}/suggestions/stream` and refetches
 * on any `suggestion` event. When SSE is unavailable (browser without
 * EventSource, or the endpoint returns an error) we fall back to the
 * initial fetch only — no polling — so we don't burn cycles on old
 * browsers.
 *
 * Feb-2026 iter (SSE robustness pass):
 *   * `refresh()` is now debounced by 200 ms so a burst of events from
 *     multiple reviewers coalesces into a single refetch instead of a
 *     round-trip per event.
 *   * We listen for the new `stale` event — the backend collapses a
 *     saturated per-subscriber queue to that single marker so the
 *     client refetches ONCE and drops the backlog. Prevents the
 *     "slow output from a stalled queue" pattern.
 *   * EventSource automatically retries on error; we just track the
 *     transport state so the UI can render a live/reconnecting dot.
 *
 * We deliberately fetch only `status=pending` — accepted suggestions
 * are already baked into the chapter body, and declined ones shouldn't
 * clutter the manuscript.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useChapterSuggestions({ projectId, chapterId, refreshKey = 0 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const refreshTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!projectId || !chapterId) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/projects/${projectId}/suggestions`,
        {
          params: { chapter_id: chapterId, status: 'pending' },
          withCredentials: true,
        },
      );
      setSuggestions(Array.isArray(data) ? data : []);
    } catch (_e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, chapterId]);

  // Debounced refresh — coalesces burst events (multiple reviewers
  // typing in parallel, or the pubsub `stale` marker after a saturated
  // queue) into a single refetch every 200 ms.
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refresh();
    }, 200);
  }, [refresh]);

  useEffect(() => {
    refresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refresh, refreshKey]);

  useEffect(() => {
    if (!projectId || !chapterId) return undefined;
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined;
    }
    const url = `${API}/projects/${projectId}/chapters/${chapterId}/suggestions/stream`;
    let es;
    try {
      es = new window.EventSource(url, { withCredentials: true });
    } catch (_e) {
      return undefined;
    }
    const onOpen         = () => setLiveConnected(true);
    const onError        = () => setLiveConnected(false);
    const onSuggestion   = () => scheduleRefresh();
    const onStale        = () => scheduleRefresh();
    es.addEventListener('open',       onOpen);
    es.addEventListener('error',      onError);
    es.addEventListener('suggestion', onSuggestion);
    es.addEventListener('stale',      onStale);
    return () => {
      es.removeEventListener('open',       onOpen);
      es.removeEventListener('error',      onError);
      es.removeEventListener('suggestion', onSuggestion);
      es.removeEventListener('stale',      onStale);
      es.close();
      setLiveConnected(false);
    };
  }, [projectId, chapterId, scheduleRefresh]);

  return { suggestions, loading, refresh, liveConnected };
}
